import { DiagnosticsFooter, EmptyState, usePayload } from './primitives';
import styles from './styles.module.css';
import s from './AudienceViewer.module.css';

const AFFORDABILITY_CLASS = {
  'Budget':       s.afBudget,
  'Mass Market':  s.afMassMarket,
  'Middle Income':s.afMiddle,
  'Affluent':     s.afAffluent,
  'Luxury':       s.afLuxury,
};

const Evidence = ({ items }) => {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <ul className={s.evidence}>
      {items.map((e, i) => <li key={i} className={s.evidenceItem}>{e}</li>)}
    </ul>
  );
};

const AudienceViewer = ({ report }) => {
  const payload = usePayload(report);
  if (!report) return <EmptyState />;

  const fields = Array.isArray(payload?.fields) ? payload.fields : [];
  const byKey = Object.fromEntries(fields.map((f) => [f.key, f]));

  const primary       = byKey['primary_target_audience'];
  const secondary     = byKey['secondary_target_audience'];
  const age           = byKey['age_or_life_stage'];
  const gender        = byKey['gender_orientation'];
  const useCases      = byKey['top_use_cases'];
  const motivations   = byKey['top_purchase_motivations'];
  const affordability = byKey['buyer_affordability_level'];
  const problem       = byKey['problem_solved'];

  const summary    = payload?.summary || null;
  const confidence = payload?.diagnostics?.profile_confidence_level || null;
  const missing    = Array.isArray(payload?.diagnostics?.missing_data_flags)
    ? payload.diagnostics.missing_data_flags : [];

  const useCaseValues    = Array.isArray(useCases?.value) ? useCases.value : [];
  const motivationValues = Array.isArray(motivations?.value) ? motivations.value : [];

  return (
    <div className={styles.viewer}>

      {/* Summary */}
      {summary && (
        <div className={s.summaryBlock}>
          <div className={s.summaryHeader}>
            <span className={s.summaryTitle}>Summary</span>
            {confidence && (
              <span className={`${s.confidenceBadge} ${confidence === 'High' ? s.confHigh : confidence === 'Medium' ? s.confMedium : s.confLow}`}>
                {confidence}
              </span>
            )}
          </div>
          <p className={s.summaryText}>{summary}</p>
        </div>
      )}

      {/* Audience identity */}
      <div className={s.block}>
        <div className={s.blockHeader}>
          <span className={s.blockTitle}>Audience Identity</span>
        </div>

        {/* Primary */}
        {primary?.value && (
          <div className={s.audienceRow}>
            <div className={s.audienceLeft}>
              <span className={s.audienceRowLabel}>Primary audience</span>
              <span className={s.audienceBadge}>{primary.value}</span>
              <Evidence items={primary.evidence} />
            </div>
            {secondary?.value && (
              <div className={s.audienceLeft} style={{ alignItems: 'flex-end' }}>
                <span className={s.audienceRowLabel}>Secondary</span>
                <span className={s.audienceBadgeSecondary}>{secondary.value}</span>
                <Evidence items={secondary.evidence} />
              </div>
            )}
          </div>
        )}

        {/* Age / Gender */}
        {(age?.value || gender?.value) && (
          <div className={`${s.audienceRow} ${!problem?.value ? s.audienceRowLast : ''}`}>
            {age?.value && (
              <div className={s.audienceLeft}>
                <span className={s.audienceRowLabel}>Age / Life stage</span>
                <div className={s.audienceMeta}>
                  <span className={s.metaChip}>{age.value}</span>
                </div>
                <Evidence items={age.evidence} />
              </div>
            )}
            {gender?.value && (
              <div className={s.audienceLeft} style={{ alignItems: 'flex-end' }}>
                <span className={s.audienceRowLabel}>Gender</span>
                <div className={s.audienceMeta}>
                  <span className={s.metaChip}>{gender.value}</span>
                </div>
                <Evidence items={gender.evidence} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Use cases + Purchase motivations */}
      {(useCaseValues.length > 0 || motivationValues.length > 0) && (
        <div className={s.block}>
          <div className={s.blockHeader}>
            <span className={s.blockTitle}>Purchase Behavior</span>
          </div>
          <div className={s.tagsGrid}>
            {useCaseValues.length > 0 && (
              <div className={s.tagCell}>
                <span className={s.tagCellLabel}>Use cases</span>
                <div className={s.tagChips}>
                  {useCaseValues.map((v, i) => (
                    <span key={i} className={`${s.tagChip} ${s.tagChipUseCase}`}>{v}</span>
                  ))}
                </div>
                <Evidence items={useCases.evidence} />
              </div>
            )}
            {motivationValues.length > 0 && (
              <div className={s.tagCell}>
                <span className={s.tagCellLabel}>Purchase motivations</span>
                <div className={s.tagChips}>
                  {motivationValues.map((v, i) => (
                    <span key={i} className={`${s.tagChip} ${s.tagChipMotivation}`}>{v}</span>
                  ))}
                </div>
                <Evidence items={motivations.evidence} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Affordability + Problem solved */}
      <div className={s.bottomRow}>
        {affordability?.value && (
          <div className={s.affordabilityCard}>
            <span className={s.affordabilityLabel}>Buyer affordability</span>
            <span className={`${s.affordabilityValue} ${AFFORDABILITY_CLASS[affordability.value] || ''}`}>
              {affordability.value}
            </span>
          </div>
        )}
        {problem?.value && (
          <div className={s.problemCard}>
            <span className={s.problemLabel}>Problem solved</span>
            <p className={s.problemText}>{problem.value}</p>
            <Evidence items={problem.evidence} />
          </div>
        )}
      </div>

      <DiagnosticsFooter missingFlags={missing} />
    </div>
  );
};

export default AudienceViewer;
